<script setup lang="ts">
/**
 * The first steps of a new partner, at the top of the dashboard: share the
 * link, optionally make a discount code, fill in where payouts go, and wait
 * for the first sale. Each step ticks itself off from the partner's own data
 * (clicks, code, payout details, orders), so there is nothing to mark by
 * hand. It goes away once every step is done, or when the partner hides it.
 */
const props = defineProps<{
  clicks: number
  hasDiscountCode: boolean
  hasPayoutDetails: boolean
  orders: number
}>()

const emit = defineEmits<{ (e: 'go', target: 'link' | 'materials' | 'discount' | 'payout'): void }>()

const { t } = useI18n()

const HIDDEN_KEY = 'affiliateGettingStartedHidden'
const hidden = ref(true)
onMounted(() => {
  try {
    hidden.value = localStorage.getItem(HIDDEN_KEY) === '1'
  } catch {
    hidden.value = false
  }
})
function hide() {
  hidden.value = true
  try {
    localStorage.setItem(HIDDEN_KEY, '1')
  } catch {
    // Storage blocked: hidden until the next visit.
  }
}

const steps = computed(() => [
  { id: 'share', done: props.clicks > 0, action: 'link' as const, extra: 'materials' as const },
  { id: 'discount', done: props.hasDiscountCode, action: 'discount' as const, optional: true },
  { id: 'payout', done: props.hasPayoutDetails, action: 'payout' as const },
  { id: 'sale', done: props.orders > 0 },
])
const doneCount = computed(() => steps.value.filter(step => step.done).length)
const visible = computed(() => !hidden.value && doneCount.value < steps.value.length)
</script>

<template>
  <div v-if="visible" class="rounded-[0.625rem] border border-accent bg-accent-soft/10 p-6 dark:bg-accent/10">
    <div class="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 class="!text-xl">{{ t('affiliate.start.title') }}</h2>
        <p class="mt-1 text-sm text-muted dark:text-gray-400">{{ t('affiliate.start.progress', { done: doneCount, total: steps.length }) }}</p>
      </div>
      <button type="button" class="link text-sm" @click="hide">{{ t('affiliate.start.hide') }}</button>
    </div>

    <ol class="mt-5 space-y-4">
      <li v-for="(step, index) in steps" :key="step.id" class="flex gap-4">
        <span
          class="flex size-8 shrink-0 items-center justify-center rounded-full font-title font-bold"
          :class="step.done ? 'bg-green-600 text-white' : 'bg-accent-soft/40 text-ink dark:bg-accent/20 dark:text-accent-soft'"
          :aria-label="step.done ? t('affiliate.start.done') : undefined"
        >
          <i v-if="step.done" class="bi bi-check-lg" aria-hidden="true" />
          <template v-else>{{ index + 1 }}</template>
        </span>
        <div class="min-w-0 flex-1 pt-1">
          <div class="font-semibold text-ink dark:text-white" :class="{ 'line-through opacity-60': step.done }">
            {{ t(`affiliate.start.steps.${step.id}.title`) }}
            <span v-if="step.optional" class="ml-1 text-xs font-normal text-muted dark:text-gray-400">{{ t('affiliate.start.optional') }}</span>
          </div>
          <p v-if="!step.done" class="mt-1 text-sm text-body dark:text-gray-300">{{ t(`affiliate.start.steps.${step.id}.text`) }}</p>
          <div v-if="!step.done && step.action" class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
            <button type="button" class="btn btn-dark btn-sm !w-auto" @click="emit('go', step.action)">
              {{ t(`affiliate.start.steps.${step.id}.action`) }}
            </button>
            <button v-if="step.extra" type="button" class="link text-sm" @click="emit('go', step.extra)">
              {{ t(`affiliate.start.steps.${step.id}.extra`) }}
            </button>
          </div>
        </div>
      </li>
    </ol>
  </div>
</template>
